/**
 * Formatters for log output
 * Handles formatting of log messages for console output
 */

import type { LogContext, LogEntry } from './types';

/**
 * ANSI color codes for terminal output
 */
const COLORS = {
  reset: '\x1b[0m',
  bright: '\x1b[1m',
  dim: '\x1b[2m',
  // Text colors
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
  white: '\x1b[37m',
  gray: '\x1b[90m',
} as const;

/**
 * Formats a timestamp for log output
 */
export function formatTimestamp(date: Date = new Date()): string {
  return date.toISOString().substring(11, 23); // HH:mm:ss.SSS
}

/**
 * Formats the log level with appropriate color
 */
export function formatLevel(level: string): string {
  const levelColors = {
    ERROR: COLORS.red,
    WARN: COLORS.yellow,
    INFO: COLORS.blue,
    DEBUG: COLORS.gray,
    TRACE: COLORS.dim + COLORS.gray,
  };

  const color = levelColors[level as keyof typeof levelColors] || COLORS.white;
  const paddedLevel = level.padEnd(5);
  return `${color}${paddedLevel}${COLORS.reset}`;
}

/**
 * Formats the logger prefix (L1-L5 or domain)
 */
export function formatPrefix(prefix: string): string {
  // Color code by level
  if (prefix.startsWith('L1')) return `${COLORS.magenta}${prefix.padEnd(15)}${COLORS.reset}`;
  if (prefix.startsWith('L2')) return `${COLORS.cyan}${prefix.padEnd(15)}${COLORS.reset}`;
  if (prefix.startsWith('L3')) return `${COLORS.green}${prefix.padEnd(15)}${COLORS.reset}`;
  if (prefix.startsWith('L4')) return `${COLORS.yellow}${prefix.padEnd(15)}${COLORS.reset}`;
  if (prefix.startsWith('L5')) return `${COLORS.blue}${prefix.padEnd(15)}${COLORS.reset}`;

  // Domain loggers
  return `${COLORS.bright}${prefix.padEnd(15)}${COLORS.reset}`;
}

/**
 * Formats context object for display
 */
export function formatContext(context: LogContext): string {
  if (Object.keys(context).length === 0) return '';

  const entries = Object.entries(context)
    .map(([key, value]) => {
      const formattedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return `${key}: ${formattedValue}`;
    })
    .join(', ');

  return `\n    ${COLORS.dim}${entries}${COLORS.reset}`;
}

/**
 * Formats file location for display
 */
export function formatLocation(file?: string, line?: number): string {
  if (!file) return '';
  const location = line ? `${file}:${line}` : file;
  return ` ${COLORS.dim}[${location}]${COLORS.reset}`;
}

/**
 * Formats a complete log entry
 */
export function formatLogEntry(
  entry: LogEntry,
  config: {
    showTimestamp: boolean;
    showContext: boolean;
    showLocation: boolean;
  }
): string {
  const parts: string[] = [];

  if (config.showTimestamp) {
    parts.push(formatTimestamp(new Date(entry.timestamp)));
  }

  parts.push(formatLevel(entry.level.toUpperCase()));

  if (entry.phase) {
    parts.push(`[${entry.phase}]`);
  }

  if (entry.domain) {
    parts.push(formatPrefix(`Domain:${entry.domain}`));
  }

  parts.push(entry.message);

  if (config.showLocation && (entry.file || entry.line)) {
    parts.push(formatLocation(entry.file, entry.line));
  }

  let output = parts.join(' ');

  if (config.showContext && entry.context && Object.keys(entry.context).length > 0) {
    output += formatContext(entry.context);
  }

  return output;
}

/**
 * Formats a phase transition message
 */
export function formatPhaseTransition(
  action: 'enter' | 'exit',
  phaseName: string,
  metadata?: LogContext
): string {
  const icon = action === 'enter' ? '→' : '←';
  const color = action === 'enter' ? COLORS.green : COLORS.blue;

  let message = `${color}${icon} Phase ${action}: ${phaseName}${COLORS.reset}`;

  if (metadata && Object.keys(metadata).length > 0) {
    message += formatContext(metadata);
  }

  return message;
}
